import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { convertUnit } from '../utils/conversions';

const { FiCopy, FiCheckCircle } = FiIcons;

const UnitConverter = ({ category, data, onCopy, copiedValue }) => {
  const [inputValue, setInputValue] = useState('1');
  const [selectedUnit, setSelectedUnit] = useState(Object.keys(data.units)[0]);
  const [results, setResults] = useState({});

  useEffect(() => {
    calculateConversions();
  }, [inputValue, selectedUnit, category]);

  const calculateConversions = () => {
    const value = parseFloat(inputValue) || 0;
    const newResults = {};
    
    // First add the selected unit itself to the results
    newResults[selectedUnit] = value;
    
    // Then add all other unit conversions
    Object.keys(data.units).forEach(unit => {
      if (unit !== selectedUnit) {
        newResults[unit] = convertUnit(value, selectedUnit, unit, data.units);
        
        // Track conversion for analytics
        if (value > 0 && window.gtag) {
          window.gtag('event', 'conversion', {
            'event_category': 'Unit Conversion',
            'conversion_id': `${category}_conversion`,
            'unit_from': selectedUnit,
            'unit_to': unit,
            'value': value
          });
        }
      }
    });
    setResults(newResults);
  };

  const formatResult = (value) => {
    if (value === 0) return '0';
    
    const absValue = Math.abs(value);
    
    // Verwende Exponentialschreibweise nur für wirklich extreme Werte
    if (absValue >= 1e21 || (absValue < 1e-21 && absValue !== 0)) {
      return value.toExponential(15);
    }
    
    // Für alle anderen Zahlen, volle Präzision anzeigen
    let formattedValue;
    
    // Maximale Anzahl der signifikanten Stellen
    const significantDigits = 16;
    
    // Für sehr kleine Zahlen (nahe 0)
    if (absValue < 0.0000001) { // Kleine Zahlen mit vielen führenden Nullen
      // In String umwandeln und führende Nullen zählen
      const decimalStr = absValue.toFixed(25); // Großzügige Anzahl an Dezimalstellen
      const decimalParts = decimalStr.split('.');
      
      if (decimalParts.length > 1) {
        const decimalPart = decimalParts[1];
        let leadingZeros = 0;
        
        for (let i = 0; i < decimalPart.length; i++) {
          if (decimalPart[i] === '0') {
            leadingZeros++;
          } else {
            break;
          }
        }
        
        // Signifikante Stellen berechnen (führende Nullen + gewünschte signifikante Stellen)
        const totalPrecision = leadingZeros + significantDigits;
        formattedValue = absValue.toFixed(totalPrecision);
        
        // Nachfolgende Nullen entfernen
        while (formattedValue.endsWith('0')) {
          formattedValue = formattedValue.slice(0, -1);
        }
        
        // Dezimalpunkt am Ende entfernen, falls vorhanden
        if (formattedValue.endsWith('.')) {
          formattedValue = formattedValue.slice(0, -1);
        }
      } else {
        formattedValue = absValue.toString();
      }
    } else if (absValue >= 1) {
      // Für Zahlen >= 1, verwende toPrecision für signifikante Stellen
      formattedValue = absValue.toPrecision(significantDigits);
      
      // Nachfolgende Nullen nach dem Dezimalpunkt entfernen
      if (formattedValue.includes('.')) {
        formattedValue = parseFloat(formattedValue).toString();
      }
    } else {
      // Für Zahlen zwischen 0.0000001 und 1
      // Zähle signifikante Stellen nach der ersten Nicht-Null-Ziffer
      const decimalStr = absValue.toString();
      const decimalParts = decimalStr.split('.');
      
      if (decimalParts.length > 1) {
        const decimalPart = decimalParts[1];
        let leadingZeros = 0;
        
        for (let i = 0; i < decimalPart.length; i++) {
          if (decimalPart[i] === '0') {
            leadingZeros++;
          } else {
            break;
          }
        }
        
        // Anzahl der anzuzeigenden Dezimalstellen berechnen
        const totalPrecision = leadingZeros + significantDigits;
        formattedValue = absValue.toFixed(totalPrecision);
        
        // Nachfolgende Nullen entfernen
        while (formattedValue.endsWith('0')) {
          formattedValue = formattedValue.slice(0, -1);
        }
        
        // Dezimalpunkt am Ende entfernen, falls vorhanden
        if (formattedValue.endsWith('.')) {
          formattedValue = formattedValue.slice(0, -1);
        }
      } else {
        formattedValue = absValue.toPrecision(significantDigits);
      }
    }
    
    // Vorzeichen wieder hinzufügen, wenn die Zahl negativ war
    if (value < 0) {
      formattedValue = '-' + formattedValue;
    }
    
    // Verwende deutsches Zahlenformat (Komma als Dezimaltrennzeichen)
    return formattedValue.replace('.', ',');
  };

  // Check if a value is rounded (has been formatted/precision limited)
  const isValueRounded = (originalValue) => {
    // For the selected unit, the value is exact
    if (originalValue === parseFloat(inputValue)) return false;
    
    // Check if the formatted value is different from the original value
    // Use a more precise comparison method
    const originalStr = originalValue.toString();
    const formattedStr = parseFloat(originalValue.toPrecision(16)).toString();
    
    return originalStr !== formattedStr;
  };
  
  const handleUnitChange = (e) => {
    const newUnit = e.target.value;
    setSelectedUnit(newUnit);
    
    // Track unit change event
    if (window.gtag) {
      window.gtag('event', 'Unit Changed', {
        event_category: 'Interaction',
        event_label: `${category} - ${newUnit}`
      });
    }
  };
  
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    
    // Track input value change (debounced to avoid too many events)
    if (e.target.value && parseFloat(e.target.value) > 0 && window.gtag) {
      window.gtag('event', 'Value Changed', {
        event_category: 'Interaction',
        event_label: category,
        value: parseFloat(e.target.value)
      });
    }
  };

  // Use the original order of units from the data object
  const unitsList = Object.keys(data.units);

  // Keep the results in the same order as the dropdown selection
  // Now including the selected unit itself
  const orderedResults = unitsList.map(unit => [unit, results[unit] || 0]);

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          {data.name}
        </h2>

        {/* Input Section */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wert eingeben
            </label>
            <input
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              placeholder="Wert eingeben"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Von Einheit
            </label>
            <select
              value={selectedUnit}
              onChange={handleUnitChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
            >
              {unitsList.map(unit => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orderedResults.map(([unit, value]) => {
            const rounded = isValueRounded(value);
            const isSelectedUnit = unit === selectedUnit;
            
            return (
              <motion.div
                key={unit}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`rounded-xl p-4 border ${
                  isSelectedUnit
                    ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-100'
                    : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-gray-600">{unit}</span>
                  <button
                    onClick={() => {
                      onCopy(formatResult(value));
                      if (window.gtag) {
                        window.gtag('event', 'Copy Result', {
                          event_category: 'Interaction',
                          event_label: `${category} - ${unit}`,
                          value: value
                        });
                      }
                    }}
                    className={`${
                      isSelectedUnit ? 'text-green-600 hover:text-green-800' : 'text-blue-600 hover:text-blue-800'
                    } transition-colors`}
                  >
                    <SafeIcon icon={copiedValue === formatResult(value) ? FiCheckCircle : FiCopy} className="text-lg" />
                  </button>
                </div>
                <div className="text-xl font-bold text-gray-800 break-all">
                  {formatResult(value)}
                </div>
                {/* Display conversion formula only for non-selected units */}
                {!isSelectedUnit && (
                  <div className="text-xs text-gray-500 mt-1">
                    1 {selectedUnit} = {formatResult(value / (parseFloat(inputValue) || 1))} {unit}
                  </div>
                )}
                {/* Display whether the value is rounded or exact */}
                <div className={`text-xs mt-1 ${rounded ? 'text-red-500' : 'text-green-500'}`}>
                  {rounded ? 'gerundet' : 'exakt'}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default UnitConverter;